import { definePreset } from "./helpers";

import type { AnimationPreset } from "./types";

/**
 * Generated by "Copy → Screen" — one clip per baked element, each holding the
 * page it came from. Shows its page for one slot and hides it otherwise, so
 * the whole baked group reads as a flipbook.
 *
 * Zero-duration `set`s (not tweens) because export seeks the timeline with
 * `runtime.sampleAt()`; sets resolve deterministically at any seek position.
 *
 * Two boundary rules keep exported loops clean:
 *  - the book TILES across `cfg.duration` when `slotMs` says its own cycle is
 *    shorter (a 2-page book baked onto a screen held open by a 3-page one
 *    repeats instead of stopping on its last page), and
 *  - the state AT `cfg.duration` is made identical to the state at 0, because
 *    that instant is the loop seam. Without it the final sample of an export
 *    lands after every page's trailing hide and renders the screen blank for
 *    one frame.
 *
 * Not shown in the gallery — see HIDDEN_CATEGORIES in ./types.
 */
export const flipbookPresets: AnimationPreset[] = [
  definePreset(
    "flipbookPlay",
    "Flipbook",
    "flipbook",
    {
      params: { pageIndex: 0, pageCount: 1, slotMs: 0 },
      schema: [
        { key: "pageIndex", label: "Page", min: 0, max: 999, step: 1 },
        { key: "pageCount", label: "Pages", min: 1, max: 1000, step: 1 },
        { key: "slotMs", label: "Slot (ms)", min: 0, max: 10000, step: 1 },
      ],
      repeat: -1,
    },
    (tl, target, _element, cfg) => {
      const pageCount = Math.max(1, Math.round(cfg.params.pageCount ?? 1));
      const pageIndex = Math.min(
        pageCount - 1,
        Math.max(0, Math.round(cfg.params.pageIndex ?? 0)),
      );
      // cfg.duration is ms; GSAP positions are seconds
      const total = cfg.duration / 1000;
      // slotMs 0 (or absent) = the classic one-cycle-fills-the-duration form
      const slotMs = Math.max(0, cfg.params.slotMs ?? 0);
      const slot = slotMs > 0 ? slotMs / 1000 : total / pageCount;
      const cycle = slot * pageCount;
      // float slop: a slot boundary computed as k*slot can land a hair past
      // `total` and add a stray repeat
      const eps = slot * 1e-6;

      // Pages after the first are `hidden` in the scene so a screen at rest
      // shows page 1 alone instead of every page stacked up. revealHidden
      // opts them back in for as long as this clip is driving them (it is
      // never unset, so it covers the whole cycle); the opacity sets below
      // still decide which single page is actually visible at any instant.
      tl.set(target, { revealHidden: 1 }, 0);

      // AnimationRuntime.compile() forces one real render right after
      // building the timeline, so this position-0 set renders correctly
      // even on a runtime's very first sampleAt(0) — see runtime.ts.
      tl.set(target, { opacity: pageIndex === 0 ? 1 : 0 }, 0);

      for (let start = pageIndex * slot; start < total - eps; start += cycle) {
        if (start > 0) {
          tl.set(target, { opacity: 1 }, start);
        }
        tl.set(target, { opacity: 0 }, Math.min(start + slot, total));
      }

      // the loop seam: same pose as t=0. Inserted last, so at the shared
      // position it wins over any trailing hide above (GSAP applies
      // same-position sets in insertion order). It is also what pads every
      // element's sub-timeline out to the full cycle length.
      tl.set(target, { opacity: pageIndex === 0 ? 1 : 0 }, total);
    },
  ),
];
